import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Plus } from '@styled-icons/bootstrap';
import Loader from 'Components/Forms/Loader';
import SearchToolbar from 'Components/Forms/SearchToolbar';
import ProductSummarySkeleton from 'FeatureModules/ProductPage/Components/Tables/ProductSummarySkeleton';
import { CardIllustration, EmptyStateCard, ThemeButton } from 'DesignSystem';
import React, { FC, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  EBackgroundTypes,
  EIllustrationTypes,
  EItemTypes,
} from 'DesignSystem/types/background.types';
import { EAvailableSizes } from 'DesignSystem/types/size.types';
import useSupplyChains from 'FeatureModules/SupplyChain/hooks/useSupplyChains';
import SupplyChainTable from 'FeatureModules/SupplyChain/Components/SupplyChainTable';
import { ITableRowAction } from 'FeatureModules/SupplyChain/Components/TableRow';
import useProductActions from 'FeatureModules/ProductPage/hooks/useProductActions';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import ActionContainer from 'Components/ActionContainer';
import EmptySearchResults from 'Components/EmptySearchResults';
import ErrorState from 'Components/ErrorState';
import { IBaseProduct } from 'FeatureModules/ProductPage/types/product.types';

interface ISupplyChainsProps {
  isArchived?: boolean;
}

const Container = styled('div')(() => ({
  position: 'relative',
  minHeight: 50,
  marginTop: 16,

  '& .MuiGrid-item': {
    display: 'flex',
  },
}));

const SupplyChains: FC<ISupplyChainsProps> = ({ isArchived }) => {
  const {
    productsWithSupplyChains,
    searchTerm,
    loading,
    error,
    hasNextPage,
    handlePageEndReached,
    setSearchTerm,
  } = useSupplyChains({ isArchived });
  const {
    handleCreateProduct,
    handleDuplicateProduct,
    navigateToProductEditor,
    handleArchiveProduct,
    handleUnarchiveProduct,
  } = useProductActions();
  const navigate = useNavigate();

  const tableActions: ITableRowAction[] = [
    {
      renderIcon: isArchived
        ? () => <Icon name="unarchive" />
        : () => <Icon name="archive" size="small" />,
      tooltip: isArchived ? 'Unarchive product' : 'Archive product',
      onClick: ({ id }) => (isArchived ? handleUnarchiveProduct(id) : handleArchiveProduct(id)),
    },
    {
      renderIcon: () => <Icon name="duplicate" size="large" />,
      tooltip: 'Duplicate product page',
      onClick: handleDuplicateProduct,
    },
    {
      renderIcon: () => <Icon name="edit" />,
      tooltip: 'Edit product page',
      onClick: ({ id }) => navigateToProductEditor(id),
    },
  ];

  const onClickSupplyChain = (product: IBaseProduct) => {
    navigate(`/supply-chains/active/${product.id}/${product.supplyChainId}`);
  };

  if (error) {
    return <ErrorState />;
  }

  return (
    <>
      <Container>
        {!productsWithSupplyChains.length && !loading && !searchTerm ? (
          <Box mt={4}>
            <EmptyStateCard
              title="Add your products to start mapping their supply chains"
              onCreateItemClick={handleCreateProduct}
              variant={EItemTypes.PRODUCT}
              buttonText="Add product"
              background={EBackgroundTypes.WORLD}
              size={EAvailableSizes.LARGE}
              renderIllustration={
                <CardIllustration
                  image={EIllustrationTypes.LOCATIONS}
                  width={300}
                  right={25}
                  top={0}
                />
              }
              data-testid="supply-chain-getting-started-card"
            />
          </Box>
        ) : (
          <Fragment>
            <ActionContainer>
              <SearchToolbar
                setSearchTerm={setSearchTerm}
                searchTerm={searchTerm || ''}
                className="search-input"
                data-testid="supply-chain-search-input"
                placeholder="Search"
                delay={500}
                actions={
                  <Box ml={1}>
                    <ThemeButton
                      size="medium"
                      color="PRIMARY"
                      onClick={handleCreateProduct}
                      startIcon={<Plus size={20} color="white" />}
                      data-testid="create-product-btn"
                    >
                      Add new product
                    </ThemeButton>
                  </Box>
                }
              />
            </ActionContainer>
            {loading && <Loader relativeHeightToScreen />}
            {!loading && productsWithSupplyChains.length > 0 && (
              <SupplyChainTable
                products={productsWithSupplyChains}
                data-testid="supply-chain-table"
                actions={tableActions}
                hasNextPage={hasNextPage}
                isArchived={isArchived}
                onPageEndReached={handlePageEndReached}
                onRowClick={onClickSupplyChain}
              />
            )}
          </Fragment>
        )}
      </Container>
      {/* No Result container */}
      {!loading && !productsWithSupplyChains?.length && !!searchTerm ? (
        <EmptySearchResults
          message="No search results found"
          action={{
            label: 'Add new product',
            onClick: handleCreateProduct,
          }}
          skeletonComponent={<ProductSummarySkeleton title={searchTerm} />}
        />
      ) : undefined}
    </>
  );
};

export default SupplyChains;
